import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { PROJECT_STAGE, PROJECT_LOST_REASONS } from "@/lib/statusLabels";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FolderKanban, Loader2, Plus } from "lucide-react";
import { useDealerContext } from "@/lib/useDealerContext";

const PROJECT_TYPES = ["Residential", "Commercial", "Hospitality", "Other"];
const TIMEFRAMES = ["ASAP / less than 30 days", "1-3 months", "3-6 months", "6-12 months", "12+ months", "Not sure yet"];
const CLOSED_STAGES = ["COMPLETED", "LOST_CLOSED"];

function MilestoneChip({ label, status }) {
  const active = status && status !== "NONE";
  return (
    <span
      className={`badge-status ${active ? "badge-available" : "badge-neutral"}`}
      data-testid={`milestone-${label.toLowerCase()}`}
    >
      {label}{active ? ` · ${status.replace(/_/g, " ")}` : ""}
    </span>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", projected_sqft: "", waste_pct: 15, client_name: "", designer_name: "", project_type: "", expected_timeframe: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { me, locations, isManager, myLocationId, defaultLocationId, showLocationPicker, locLabel } = useDealerContext();
  const [locationId, setLocationId] = useState("");
  const myRole = me?.dealer_role || (me?.location_scope === "location" ? "LOCATION_MANAGER" : "DEALER_ADMIN");
  // Phase 0: "My Projects" is the natural default for Sales/Designer; everyone can
  // still browse every project in their permitted location scope.
  const [mine, setMine] = useState(null); // null = waiting for me
  const effectiveMine = mine ?? myRole === "SALES_DESIGNER";

  useEffect(() => {
    if (!locationId) setLocationId(isManager ? (myLocationId || "") : (defaultLocationId || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager, myLocationId, defaultLocationId]);

  useEffect(() => {
    if (me && mine === null) setMine(myRole === "SALES_DESIGNER");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const load = (onlyMine = effectiveMine) => {
    api
      .get("/projects", { params: onlyMine ? { mine: 1 } : {} })
      .then((res) => setProjects(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (mine !== null) load(mine); }, [mine]);

  const [dupMatches, setDupMatches] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // HRA51-020 / D3: duplicate detection before create — warn once, then the user may create anyway
      if (dupMatches === null) {
        const lm = await api.get("/projects/likely-matches", { params: { name: form.name, client_name: form.client_name || undefined } });
        if (Array.isArray(lm.data) && lm.data.length) {
          setDupMatches(lm.data);
          setSaving(false);
          return;
        }
      }
      const { data } = await api.post("/projects", {
        ...form,
        projected_sqft: parseFloat(form.projected_sqft),
        waste_pct: parseInt(form.waste_pct, 10),
        ...(locationId ? { location_id: locationId } : {}),
      });
      toast.success(`Project "${data.name}" created`);
      setOpen(false);
      setDupMatches(null);
      navigate(`/dealer/projects/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="dealer-projects-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Client work</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Projects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-button" className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border bg-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">Create project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="mt-2 space-y-5" data-testid="new-project-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project name *</Label>
                <Input required data-testid="project-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Coral Gables Residence" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Projected sqft *</Label>
                  <Input required type="number" step="0.1" min="1" data-testid="project-sqft-input" value={form.projected_sqft} onChange={(e) => setForm((f) => ({ ...f, projected_sqft: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Overage %</Label>
                  <Input type="number" min="0" max="100" step="1" data-testid="project-waste-select" value={form.waste_pct} onChange={(e) => setForm((f) => ({ ...f, waste_pct: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                  <p className="text-[10px] text-muted-foreground">Recommended overage: 15% — any value allowed, including 0%</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Client</Label>
                  <Input data-testid="project-client-input" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Designer</Label>
                  <Input data-testid="project-designer-input" value={form.designer_name} onChange={(e) => setForm((f) => ({ ...f, designer_name: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project type (optional)</Label>
                  <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
                    <SelectTrigger data-testid="project-type-select" className="h-11 bg-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Expected timeframe (optional)</Label>
                  <Select value={form.expected_timeframe} onValueChange={(v) => setForm((f) => ({ ...f, expected_timeframe: v }))}>
                    <SelectTrigger data-testid="project-timeframe-select" className="h-11 bg-white"><SelectValue placeholder="Select timeframe" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {TIMEFRAMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isManager && (
                <p className="text-xs text-muted-foreground" data-testid="project-location-fixed">
                  Location: {locLabel(myLocationId) || "your assigned location"} (assigned by Zelligio)
                </p>
              )}
              {showLocationPicker && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Location</Label>
                  <Select value={locationId} onValueChange={setLocationId}>
                    <SelectTrigger data-testid="project-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id} data-testid={`project-location-${l.id}`}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {dupMatches && dupMatches.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="project-duplicate-warning">
                  <p className="font-medium">Possible duplicate — {dupMatches.length} similar project{dupMatches.length > 1 ? "s" : ""} already exist{dupMatches.length > 1 ? "" : "s"}:</p>
                  <ul className="mt-1 list-disc pl-4">
                    {dupMatches.slice(0, 5).map((m) => <li key={m.project_id}>{m.name}{m.client_name ? ` — ${m.client_name}` : ""}{m.stage ? ` (${m.stage})` : ""}</li>)}
                  </ul>
                  <p className="mt-1">Submit again to create a new project anyway, or close to open the existing one.</p>
                </div>
              )}

              <Button type="submit" disabled={saving} data-testid="create-project-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create project"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 inline-flex rounded-md bg-sand p-1" data-testid="projects-scope-toggle">
        <button
          onClick={() => setMine(true)}
          data-testid="projects-tab-mine"
          className={`rounded px-4 py-1.5 text-sm transition-colors duration-200 ${effectiveMine ? "bg-white font-medium text-charcoal shadow-sm" : "text-warmgrey hover:text-charcoal"}`}
        >
          My projects
        </button>
        <button
          onClick={() => setMine(false)}
          data-testid="projects-tab-all"
          className={`rounded px-4 py-1.5 text-sm transition-colors duration-200 ${!effectiveMine ? "bg-white font-medium text-charcoal shadow-sm" : "text-warmgrey hover:text-charcoal"}`}
        >
          {isManager ? "All at my location" : "All projects"}
        </button>
      </div>

      {!projects || mine === null ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : projects.length === 0 ? (
        <Card className="mt-8 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <FolderKanban className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="projects-empty">
              {effectiveMine ? "No projects owned by you yet. Create one, or browse the team view." : "No projects yet. Create one to calculate quantities and reserve stock."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {projects.map((p) => (
            <Card
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => navigate(`/dealer/projects/${p.id}`)}
              className="cursor-pointer border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)] transition-all duration-200 hover:-translate-y-[2px]"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-xl font-medium text-charcoal">{p.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {p.projected_sqft} sqft · {p.waste_pct}% overage{p.line_count > 0 ? ` · ${p.line_count} planned line${p.line_count === 1 ? "" : "s"}` : ""}
                      {p.client_name ? ` · ${p.client_name}` : ""}
                      {locLabel(p.location_id) ? ` · ${locLabel(p.location_id)}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground" data-testid={`project-owner-line-${p.id}`}>
                      Owner: {p.owner_name || "Unassigned"}
                      {p.last_activity_at ? ` · active ${new Date(p.last_activity_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                    </p>
                    {p.next_step && (
                      <p className="mt-2 text-xs text-warmgrey" data-testid={`project-next-step-${p.id}`}>Next: {p.next_step}</p>
                    )}
                    {p.follow_up_date && (
                      <p className="mt-0.5 text-xs text-terracotta" data-testid={`project-follow-up-${p.id}`}>Follow up {p.follow_up_date}</p>
                    )}
                    {p.target_delivery_date && (
                      <p className="mt-0.5 text-xs text-warmgrey" data-testid={`project-target-${p.id}`}>Target delivery {p.target_delivery_date}</p>
                    )}
                    {p.stage === "LOST_CLOSED" && p.lost_reason && (
                      <p className="mt-0.5 text-xs text-status-sold" data-testid={`project-lost-${p.id}`}>Lost — {PROJECT_LOST_REASONS[p.lost_reason] || p.lost_reason}</p>
                    )}
                  </div>
                  <span className={`badge-status ${CLOSED_STAGES.includes(p.stage) ? "badge-neutral" : p.stage === "WON" ? "badge-available" : "badge-reserved"}`} data-testid={`project-stage-${p.id}`}>
                    {PROJECT_STAGE[p.stage] || p.stage}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <MilestoneChip label="Sample" status={p.sample_status} />
                  <MilestoneChip label="Quote" status={p.quote_status} />
                  <MilestoneChip label="Reservation" status={p.reservation_status} />
                  <MilestoneChip label="Order" status={p.order_status} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
